import type { VariantProps } from 'class-variance-authority';

import { forwardRef } from 'react';
import { cva } from 'class-variance-authority';
import { TriangleAlertIcon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

const inlineWarningVariants = cva('flex items-start gap-2 rounded-md border px-3 py-2.5 text-sm', {
  variants: {
    variant: {
      amber: 'border-amber bg-amber/5 text-amber',
      pink: 'border-pink bg-pink-light text-pink-dark',
      blue: 'border-blue bg-tint-light text-blue-dark',
    },
  },
  defaultVariants: {
    variant: 'amber',
  },
});

export interface InlineWarningProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof inlineWarningVariants> {
  hidden?: boolean;
  /**
   * Replaces the warning triangle. Sized and coloured by the caller, so pass the
   * same `mt-0.5 size-4 shrink-0` to keep it aligned with the first line of text.
   */
  icon?: React.ReactNode;
}

export const InlineWarning = forwardRef<HTMLDivElement, InlineWarningProps>(
  ({ children, className, hidden, variant, icon, ...props }, ref) => {
    if (hidden) {
      return null;
    }

    return (
      <div
        ref={ref}
        role="alert"
        className={twMerge(inlineWarningVariants({ variant }), className)}
        {...props}
      >
        {icon ?? <TriangleAlertIcon className="mt-0.5 size-4 shrink-0" />}
        <span>{children}</span>
      </div>
    );
  },
);

InlineWarning.displayName = 'InlineWarning';
